import React, { useMemo } from 'react';
import { toEndOfUtcDayString, toStartOfUtcDayString } from '@chainflip/utils/date';
import { formatUsdValue } from '@chainflip/utils/number';
import { type BarDatum } from '@nivo/bar';
import { differenceInDays } from 'date-fns';
import { ChartTooltip } from '@/shared/components/ChartTooltip';
import { type BarTooltipProps, StackedBarChart } from '@/shared/components/StackedBarChart';
import { type GetLpEarnedFeeAggregatesQuery } from '@/shared/graphql/generated/lp/graphql';
import { GetLpEarnedFeeAggregates } from '@/shared/graphql/lp/lp';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { getEmptyTickMap } from '@/shared/utils/charts';

type ChartAggItems = keyof typeof colors;

const colors = {
  'Order Fees': '#46DA93',
  'Boost Fees': '#FF33AF',
} as const;

const getToolTip = (props: BarTooltipProps<BarDatum>, isDaily: boolean = false) => {
  const date = new Date(props.data.date);
  const { data } = props;
  data['Boost Fees'] = data['Boost Fees'] ?? 0;
  data['Order Fees'] = data['Order Fees'] ?? 0;

  return (
    <ChartTooltip>
      <div className="flex w-[240px] flex-col items-center gap-y-2.5 text-12 text-cf-light-2">
        <div>{isDaily ? date.toLocaleString() : date.toLocaleDateString()}</div>
        {Object.keys(data)
          .filter((key) => key !== 'date')
          .map((key) => (
            <div key={key} className="flex w-full justify-between text-12 text-cf-light-2">
              <div className="flex items-center gap-x-1 font-aeonikMedium">
                <div
                  className="h-2.5 w-[3px] rounded-md"
                  style={{
                    backgroundColor: colors[key as ChartAggItems],
                  }}
                />
                {key}
              </div>
              <span className="text-cf-green-3">
                +{formatUsdValue(props.data[key as ChartAggItems])}
              </span>
            </div>
          ))}
      </div>
    </ChartTooltip>
  );
};
const parseAggregatesToChartData =
  (chartStartDate: Date, chartEndDate: Date) => (queryResult: GetLpEarnedFeeAggregatesQuery) => {
    const keys = new Set<string>(['Order Fees', 'Boost Fees']);
    const now = new Date().toISOString();
    const boostLeastDate =
      queryResult.account?.boostFees.groupedAggregates?.reduce(
        (acc, curr) => (!curr.keys?.[0] || acc < curr.keys?.[0] ? acc : curr.keys[0]),
        new Date().toISOString(),
      ) ?? now;
    const limitLeastDate =
      queryResult.account?.limitOrderFees.groupedAggregates?.reduce(
        (acc, curr) => (!curr.keys?.[0] || acc < curr.keys?.[0] ? acc : curr.keys[0]),
        new Date().toISOString(),
      ) ?? now;
    const rangeLeastDate =
      queryResult.account?.rangeOrderFees.groupedAggregates?.reduce(
        (acc, curr) => (!curr.keys?.[0] || acc < curr.keys?.[0] ? acc : curr.keys[0]),
        new Date().toISOString(),
      ) ?? now;

    const leastDate = new Date(
      Math.min(
        new Date(boostLeastDate).getTime(),
        new Date(limitLeastDate).getTime(),
        new Date(rangeLeastDate).getTime(),
      ),
    );

    const map = getEmptyTickMap(leastDate, chartEndDate, (date) => ({
      date: date.toISOString(),
      'Order Fees': 0,
      'Boost Fees': 0,
    }));

    const boostFeeAggregates = queryResult.account?.boostFees.groupedAggregates;
    for (const agg of boostFeeAggregates ?? []) {
      const date = new Date(agg.keys![0]);

      // if the date isn't in the map, it's out of range
      const entry = map.get(date.valueOf());
      if (entry) {
        map.set(date.valueOf(), {
          ...entry,
          date: date.toISOString(),
          'Boost Fees': entry['Boost Fees'] + Number(agg.sum?.feeUsd || '0'),
        });
      }
    }

    const limitOrderFeeAggregates = queryResult.account?.limitOrderFees.groupedAggregates;
    const rangeOrderFeeAggregates = queryResult.account?.rangeOrderFees.groupedAggregates;
    const poolFeeAggregates = [
      ...(limitOrderFeeAggregates ?? []),
      ...(rangeOrderFeeAggregates ?? []),
    ];
    for (const agg of poolFeeAggregates) {
      const date = new Date(agg.keys![0]);

      let orderFees = 0;

      if (agg.sum) {
        if ('baseFeeUsd' in agg.sum) orderFees += Number(agg.sum.baseFeeUsd || '0');
        if ('quoteFeeUsd' in agg.sum) orderFees += Number(agg.sum.quoteFeeUsd || '0');
        if ('feeUsd' in agg.sum) orderFees += Number(agg.sum.feeUsd || '0');
      }
      // if the date isn't in the map, it's out of range
      const entry = map.get(date.valueOf());
      if (entry) {
        map.set(date.valueOf(), {
          ...entry,
          date: date.toISOString(),
          'Order Fees': entry['Order Fees'] + orderFees,
        });
      }
    }

    return {
      data: [...map.values()],
      keys: [...keys.values()],
    };
  };

export const FeesStackedBarChart = ({
  account,
  chartStartDate,
  chartEndDate,
}: {
  account: string;
  chartStartDate: Date;
  chartEndDate: Date;
}) => {
  const daysBack = differenceInDays(chartEndDate, chartStartDate);

  const isDaily = daysBack === 1;

  const cachedParseAggregatesToChartData = useMemo(
    () => parseAggregatesToChartData(chartStartDate, new Date()),
    [daysBack],
  );

  const boostShareGroupBy = isDaily
    ? 'EXECUTED_AT_TIMESTAMP_TRUNCATED_TO_HOUR'
    : 'EXECUTED_AT_TIMESTAMP_TRUNCATED_TO_DAY';
  const orderGroupBy = isDaily
    ? 'BLOCK_TIMESTAMP_TRUNCATED_TO_HOUR'
    : 'BLOCK_TIMESTAMP_TRUNCATED_TO_DAY';

  const { data: response, isLoading } = useGqlQuery(GetLpEarnedFeeAggregates, {
    variables: {
      idSs58: account,
      minTimestamp: toStartOfUtcDayString(chartStartDate),
      maxTimestamp: toEndOfUtcDayString(chartEndDate),
      boostShareGroupBy,
      limitOrderGroupBy: orderGroupBy,
      rangeOrderGroupBy: orderGroupBy,
    },
    select: cachedParseAggregatesToChartData,
    refetchInterval: 15_000,
    context: { clientName: 'lpProcessor' },
  });

  const chartData = response?.data || [];
  const chartKeys = response?.keys || [];
  return (
    <StackedBarChart
      isLoading={isLoading}
      chartData={chartData}
      isDaily={isDaily}
      keys={chartKeys}
      tooltipCallback={getToolTip}
      colorCallback={(e) => colors[e.id as ChartAggItems]}
    />
  );
};
